import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermission } from "@/auth/usePermission";
import {
  useV1InventoryPendingLotAllocationsList,
  useV1InventoryPendingLotAllocationsAllocateCreate,
  useV1InventoryPendingLotAllocationsMarkUntraceableCreate,
  useV1InventoryWarehousesList,
  useV1InventoryMaterialsList,
} from "@/api/generated/v1/v1";
import { PageBanner } from "@/shared/components/PageBanner";

export function PendingLotAllocationsPage() {
  const canManage = usePermission("inventory.manage");
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState("pending");
  const [materialFilter, setMaterialFilter] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState("");
  
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeAction, setActiveAction] = useState<"allocate" | "untraceable" | null>(null);

  // Allocate form state
  const [lotLines, setLotLines] = useState<{ lot_id: string; quantity: string }[]>([{ lot_id: "", quantity: "" }]);
  
  // Untraceable form state
  const [untraceableReason, setUntraceableReason] = useState("");

  const [error, setError] = useState<string | null>(null);

  const { data: plaData, isLoading: plaLoading, refetch: refetchPla } =
    useV1InventoryPendingLotAllocationsList(
      {
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(materialFilter ? { material: materialFilter } : {}),
        ...(warehouseFilter ? { warehouse: warehouseFilter } : {}),
      } as any,
      { query: {} }
    );

  const { data: warehousesData } = useV1InventoryWarehousesList(undefined, { query: {} });
  const { data: materialsData } = useV1InventoryMaterialsList(undefined, { query: {} });

  const { mutate: allocateLots, isPending: isAllocating } =
    useV1InventoryPendingLotAllocationsAllocateCreate({
      mutation: {
        onSuccess: () => {
          setExpandedId(null);
          setActiveAction(null);
          setError(null);
          refetchPla();
        },
        onError: (err: any) => {
          setError(
            err?.response?.data?.detail ||
            err?.response?.data?.non_field_errors?.[0] ||
            "خطا در تخصیص پالت"
          );
        },
      },
    });

  const { mutate: markUntraceable, isPending: isMarking } =
    useV1InventoryPendingLotAllocationsMarkUntraceableCreate({
      mutation: {
        onSuccess: () => {
          setExpandedId(null);
          setActiveAction(null);
          setError(null);
          refetchPla();
        },
        onError: (err: any) => {
          setError(
            err?.response?.data?.detail ||
            err?.response?.data?.reason?.[0] ||
            "خطا در ثبت نامشخص بودن"
          );
        },
      },
    });

  const allocations = plaData?.results ?? [];
  const warehouses = warehousesData?.results ?? [];
  const materials = materialsData?.results ?? [];

  const handleOpenAction = (item: any, action: "allocate" | "untraceable") => {
    setExpandedId(item.id);
    setActiveAction(action);
    setLotLines([{ lot_id: "", quantity: String(item.quantity) }]);
    setUntraceableReason("");
    setError(null);
  };

  const handleAddLine = () => {
    setLotLines([...lotLines, { lot_id: "", quantity: "" }]);
  };

  const handleLineChange = (index: number, field: "lot_id" | "quantity", value: string) => {
    const newLines = [...lotLines];
    if (newLines[index]) {
      newLines[index][field] = value;
      setLotLines(newLines);
    }
  };

  const handleRemoveLine = (index: number) => {
    const newLines = [...lotLines];
    newLines.splice(index, 1);
    setLotLines(newLines);
  };

  const handleSubmitAllocate = (id: string) => {
    setError(null);
    if (lotLines.some((l) => !l.lot_id.trim() || !l.quantity.trim())) {
      setError("شناسه پالت و تعداد برای تمام خطوط الزامی است.");
      return;
    }
    allocateLots({
      id,
      data: {
        lines: lotLines,
      } as any,
    });
  };

  const handleSubmitUntraceable = (id: string) => {
    setError(null);
    if (!untraceableReason.trim()) {
      setError("دلیل مفقودی/نامشخص بودن الزامی است.");
      return;
    }
    markUntraceable({
      id,
      data: {
        reason: untraceableReason.trim(),
      } as any,
    });
  };

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="تخصیص تعلیقی (Pending Allocations)"
        subtitle="تخصیص دیرهنگام پالت‌ها یا نامشخص‌کردن"
        icon="⏳"
        helpLink="/help/inventory/pending-allocations"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate("/inventory")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← داشبورد انبار
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>

        {/* فیلترها */}
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
          <div style={{ flex: 1, minWidth: "150px" }}>
            <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              وضعیت
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: "100%", padding: "0.6rem 0.9rem",
                background: "rgba(255,255,255,0.05)", border: "1px solid var(--glass-border)",
                borderRadius: "8px", color: "var(--color-text)", boxSizing: "border-box",
              }}
            >
              <option value="">همه وضعیت‌ها</option>
              <option value="pending">در انتظار (PENDING)</option>
              <option value="blocked">بلوکه شده (BLOCKED)</option>
              <option value="allocated">تخصیص یافته (ALLOCATED)</option>
              <option value="untraceable">نامشخص (UNTRACEABLE)</option>
            </select>
          </div>
          <div style={{ flex: 1, minWidth: "150px" }}>
            <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              متریال
            </label>
            <select
              value={materialFilter}
              onChange={(e) => setMaterialFilter(e.target.value)}
              style={{
                width: "100%", padding: "0.6rem 0.9rem",
                background: "rgba(255,255,255,0.05)", border: "1px solid var(--glass-border)",
                borderRadius: "8px", color: "var(--color-text)", boxSizing: "border-box",
              }}
            >
              <option value="">همه متریال‌ها</option>
              {materials.map((m: any) => (
                <option key={m.id} value={m.id}>{m.code}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: "150px" }}>
            <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              انبار
            </label>
            <select
              value={warehouseFilter}
              onChange={(e) => setWarehouseFilter(e.target.value)}
              style={{
                width: "100%", padding: "0.6rem 0.9rem",
                background: "rgba(255,255,255,0.05)", border: "1px solid var(--glass-border)",
                borderRadius: "8px", color: "var(--color-text)", boxSizing: "border-box",
              }}
            >
              <option value="">همه انبارها</option>
              {warehouses.map((w: any) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* لیست ردیف‌ها */}
        {plaLoading ? (
          <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری...</p>
        ) : allocations.length === 0 ? (
          <p style={{ color: "var(--color-text-muted)", textAlign: "center", padding: "2rem" }}>
            هیچ رکورد تعلیقی یافت نشد.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {allocations.map((item: any) => {
              const materialCode = materials.find((m: any) => m.id === String(item.material))?.code;
              const warehouseName = warehouses.find((w: any) => w.id === String(item.warehouse))?.name;
              const isExpanded = expandedId === item.id;
              const isPendingOrBlocked = ["pending", "blocked"].includes(item.status);

              return (
                <div
                  key={item.id}
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid var(--glass-border)",
                    borderRadius: "10px",
                    padding: "1rem",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "0.75rem" }}>
                    <div>
                      <div style={{ fontWeight: "bold", fontSize: "1rem" }} dir="ltr">
                        {item.status.toUpperCase()}
                      </div>
                      <div style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginTop: "0.25rem" }}>
                        متریال: {materialCode || "—"} · انبار: {warehouseName || "—"} · تعداد تراکنش: {item.quantity}
                      </div>
                      <div style={{ fontSize: "0.85rem", marginTop: "0.25rem" }} dir="ltr">
                        دلیل تعلیق: {item.reason}
                        {item.status === 'untraceable' && ` | دلیل مفقودی: ${item.untraceable_reason}`}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", marginTop: "0.25rem" }}>
                        تراکنش: {item.stock_transaction}
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                      {isPendingOrBlocked && canManage && (
                        <>
                          <button
                            onClick={() => handleOpenAction(item, "allocate")}
                            style={{
                              padding: "0.5rem 1rem",
                              borderRadius: "8px",
                              background: "rgba(33, 150, 243, 0.15)",
                              color: "#64b5f6",
                              border: "1px solid rgba(33, 150, 243, 0.3)",
                              cursor: "pointer",
                              fontSize: "0.85rem",
                            }}
                          >
                            تخصیص پالت
                          </button>
                          <button
                            onClick={() => handleOpenAction(item, "untraceable")}
                            style={{
                              padding: "0.5rem 1rem",
                              borderRadius: "8px",
                              background: "rgba(244, 67, 54, 0.15)",
                              color: "#e57373",
                              border: "1px solid rgba(244, 67, 54, 0.3)",
                              cursor: "pointer",
                              fontSize: "0.85rem",
                            }}
                          >
                            مفقود / نامشخص
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* پنل فرم اکشن */}
                  {isExpanded && activeAction === "allocate" && (
                    <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid var(--glass-border)" }}>
                      <h4 style={{ margin: "0 0 1rem 0", fontSize: "0.95rem" }}>تخصیص پالت(ها)</h4>
                      
                      {lotLines.map((line, index) => (
                        <div key={index} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem", alignItems: "flex-end" }}>
                          <div style={{ flex: 2 }}>
                            <label style={{ display: "block", fontSize: "0.75rem", color: "var(--color-text-muted)", marginBottom: "0.2rem" }}>
                              شناسه پالت (Lot ID)
                            </label>
                            <input
                              type="text"
                              value={line.lot_id}
                              onChange={(e) => handleLineChange(index, "lot_id", e.target.value)}
                              placeholder="00000000-0000-0000..."
                              dir="ltr"
                              style={{ width: "100%", padding: "0.5rem", background: "rgba(0,0,0,0.2)", border: "1px solid var(--glass-border)", borderRadius: "6px", color: "white" }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: "block", fontSize: "0.75rem", color: "var(--color-text-muted)", marginBottom: "0.2rem" }}>
                              تعداد (Qty)
                            </label>
                            <input
                              type="number"
                              value={line.quantity}
                              onChange={(e) => handleLineChange(index, "quantity", e.target.value)}
                              dir="ltr"
                              style={{ width: "100%", padding: "0.5rem", background: "rgba(0,0,0,0.2)", border: "1px solid var(--glass-border)", borderRadius: "6px", color: "white" }}
                            />
                          </div>
                          {lotLines.length > 1 && (
                            <button
                              onClick={() => handleRemoveLine(index)}
                              style={{ padding: "0.5rem", background: "transparent", color: "#e57373", border: "none", cursor: "pointer", fontSize: "1.2rem" }}
                            >
                              &times;
                            </button>
                          )}
                        </div>
                      ))}

                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1rem" }}>
                        <button
                          onClick={handleAddLine}
                          style={{ padding: "0.5rem 1rem", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "6px", color: "white", cursor: "pointer", fontSize: "0.85rem" }}
                        >
                          + افزودن خط پالت
                        </button>

                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                          {error && <span style={{ color: "#e57373", fontSize: "0.85rem" }}>{error}</span>}
                          <button
                            onClick={() => setExpandedId(null)}
                            style={{ padding: "0.5rem 1rem", background: "transparent", border: "1px solid var(--glass-border)", borderRadius: "6px", color: "white", cursor: "pointer" }}
                          >
                            انصراف
                          </button>
                          <button
                            onClick={() => handleSubmitAllocate(item.id)}
                            disabled={isAllocating}
                            style={{ padding: "0.5rem 1rem", background: "#4caf50", border: "none", borderRadius: "6px", color: "white", cursor: "pointer", fontWeight: "bold" }}
                          >
                            {isAllocating ? "در حال ثبت..." : "تأیید و تخصیص"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {isExpanded && activeAction === "untraceable" && (
                    <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid var(--glass-border)" }}>
                      <h4 style={{ margin: "0 0 1rem 0", fontSize: "0.95rem", color: "#e57373" }}>اعلام نامشخص بودن (Untraceable)</h4>
                      
                      <div style={{ marginBottom: "1rem" }}>
                        <label style={{ display: "block", fontSize: "0.75rem", color: "var(--color-text-muted)", marginBottom: "0.2rem" }}>
                          دلیل مفقودی / نامشخص بودن پالت
                        </label>
                        <input
                          type="text"
                          value={untraceableReason}
                          onChange={(e) => setUntraceableReason(e.target.value)}
                          placeholder="مثلاً: پالت‌ها در خط تولید ترکیب شده‌اند و قابل تفکیک نیست"
                          style={{ width: "100%", padding: "0.5rem", background: "rgba(0,0,0,0.2)", border: "1px solid var(--glass-border)", borderRadius: "6px", color: "white" }}
                        />
                      </div>

                      <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", alignItems: "center" }}>
                        {error && <span style={{ color: "#e57373", fontSize: "0.85rem" }}>{error}</span>}
                        <button
                          onClick={() => setExpandedId(null)}
                          style={{ padding: "0.5rem 1rem", background: "transparent", border: "1px solid var(--glass-border)", borderRadius: "6px", color: "white", cursor: "pointer" }}
                        >
                          انصراف
                        </button>
                        <button
                          onClick={() => handleSubmitUntraceable(item.id)}
                          disabled={isMarking}
                          style={{ padding: "0.5rem 1rem", background: "#e57373", border: "none", borderRadius: "6px", color: "white", cursor: "pointer", fontWeight: "bold" }}
                        >
                          {isMarking ? "در حال ثبت..." : "تأیید نامشخص بودن"}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* لاین‌های از قبل تخصیص یافته */}
                  {item.status === 'allocated' && item.lines && item.lines.length > 0 && (
                    <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid var(--glass-border)" }}>
                      <div style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.5rem" }}>تخصیص‌های انجام شده:</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                        {item.lines.map((l: any) => (
                          <div key={l.id} style={{ display: "flex", justifyContent: "space-between", background: "rgba(0,0,0,0.2)", padding: "0.5rem", borderRadius: "6px", fontSize: "0.85rem" }}>
                            <span dir="ltr">Lot: {l.lot}</span>
                            <span dir="ltr">Qty: {l.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
